import { useEffect, useMemo, useRef, useState } from 'react';
import { suggestRootCause } from '../ai/aiApi';
import { fetchTrainings } from '../competence/competenceApi';
import type { TrainingSession } from '../competence/types';
import {
  fetchQmsAssigneeOptions,
  patchContingencyDone,
  patchCorrectiveImplemented,
  postContingency,
  postContingencyVerification,
  postCorrectiveAction,
  postCorrectiveDecision,
  postCorrectiveEfficacy,
  postCloseCorrectiveWithoutEfficacy,
  postExternalCommunication,
  postFollowUpNote,
  postRootCauseAnalysis,
  postReview,
  postTrainingPlanLink,
  postCloseRecord,
} from './qmsApi';
import {
  labelQmsActionStatus,
  labelQmsRecordStatus,
  labelQmsReview,
  qmsRecordStatusBadgeClass,
  qmsReviewBadgeClass,
  isCorrectiveDecisionActive,
  resolveCorrectiveDecisionSummary,
  labelCorrectiveActionLifecycle,
  labelEfficacyResult,
  isCorrectiveActionClosedUi,
  labelAnalysisCycleReason,
  getActiveRootCauseCycle,
  isRootCauseAnalysisCompleteUi,
  labelContingencySummary,
  labelCorrectiveDecisionSummary,
  labelCorrectiveActionSummary,
  labelRecordClosureSummary,
  labelCloseRecordButton,
  contingencySummaryBadgeClass,
  correctiveDecisionSummaryBadgeClass,
  correctiveActionSummaryBadgeClass,
  recordClosureSummaryBadgeClass,
  canCloseRecordManually,
  type QmsStatusSummaryShape,
  type QmsOpeningDecisionInput,
  type QmsRootCauseCycleRef,
} from './qmsLabels';
import { DateCell } from '../../components/DateCell';

function SummaryBadge({ label, value, className }: { label: string; value: string; className: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={className}>{value}</span>
    </div>
  );
}

export function StatusSummaryHeader({
  openingId,
  statusSummary,
  canManage,
  onRefresh,
  onMsg,
}: {
  openingId: string;
  statusSummary?: QmsStatusSummaryShape | null;
  canManage: boolean;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  if (!statusSummary) return null;
  const canClose = canManage && canCloseRecordManually(statusSummary);

  const submitClose = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      onMsg('Escriba el motivo del cierre');
      return;
    }
    setSaving(true);
    try {
      await postCloseRecord(openingId, trimmed);
      setDialogOpen(false);
      setReason('');
      await onRefresh();
      onMsg('Caso cerrado');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">Estado del caso</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryBadge
          label="Contingencia"
          value={labelContingencySummary(statusSummary.contingency)}
          className={contingencySummaryBadgeClass(statusSummary.contingency)}
        />
        <SummaryBadge
          label="Decisión AC"
          value={labelCorrectiveDecisionSummary(statusSummary.correctiveDecision)}
          className={correctiveDecisionSummaryBadgeClass(statusSummary.correctiveDecision)}
        />
        <SummaryBadge
          label="Acción correctiva"
          value={labelCorrectiveActionSummary(statusSummary.correctiveAction)}
          className={correctiveActionSummaryBadgeClass(statusSummary.correctiveAction)}
        />
        <SummaryBadge
          label="No conformidad"
          value={labelRecordClosureSummary(statusSummary.record)}
          className={recordClosureSummaryBadgeClass(statusSummary.record)}
        />
      </div>

      {canClose && (
        <div>
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5"
            onClick={() => setDialogOpen(true)}
          >
            {labelCloseRecordButton()}
          </button>
        </div>
      )}

      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="w-full max-w-md rounded-lg border border-border bg-card p-4 space-y-3 shadow-lg"
            role="dialog"
            aria-labelledby="close-record-title"
          >
            <h3 id="close-record-title" className="font-medium text-sm">
              {labelCloseRecordButton()} — justificación obligatoria
            </h3>
            <p className="text-xs text-muted-foreground">
              Indique el motivo del cierre. Quedará registrado en el historial del caso.
            </p>
            <textarea
              className="w-full min-h-[88px] border rounded px-2 py-1 text-sm"
              placeholder="Motivo del cierre"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-sm rounded border px-3 py-1"
                onClick={() => {
                  setDialogOpen(false);
                  setReason('');
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={reason.trim().length === 0 || saving}
                className="text-sm rounded bg-primary text-primary-foreground px-3 py-1 disabled:opacity-50"
                onClick={submitClose}
              >
                Confirmar cierre
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

type ReviewChoice = 'pertinent' | 'dismissed';

export function ReviewSection({
  openingId,
  reviewOutcome,
  recordStatus,
  dismissReason: storedDismissReason,
  onDone,
  heading = 'Revisión de pertinencia',
}: {
  openingId: string;
  reviewOutcome: string;
  recordStatus: string;
  dismissReason?: string | null;
  onDone: () => Promise<void>;
  heading?: string;
}) {
  const [choice, setChoice] = useState<ReviewChoice | null>(null);
  const [dismissReasonInput, setDismissReasonInput] = useState('');
  const [saving, setSaving] = useState(false);
  const outcome = reviewOutcome || 'pending';
  const isPending = outcome === 'pending';

  useEffect(() => {
    setChoice(null);
    setDismissReasonInput('');
  }, [outcome, openingId]);

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">{heading}</h2>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Estado del registro:</span>
        <span className={qmsRecordStatusBadgeClass(recordStatus)}>{labelQmsRecordStatus(recordStatus)}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">Revisión:</span>
        <span className={qmsReviewBadgeClass(outcome)}>{labelQmsReview(outcome)}</span>
      </div>
      {!isPending && outcome === 'dismissed' && storedDismissReason ? (
        <p className="text-xs text-muted-foreground">Motivo de desestimación: {storedDismissReason}</p>
      ) : null}

      {isPending ? (
        <>
          <p className="text-sm text-muted-foreground">Elija una opción y pulse guardar.</p>
          <div role="radiogroup" aria-label="Resultado de la revisión" className="space-y-2">
            <label
              className={`flex cursor-pointer gap-3 rounded-md border p-3 text-sm transition-colors ${
                choice === 'pertinent' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/40'
              }`}
            >
              <input
                type="radio"
                name="qms-review-outcome"
                className="mt-0.5"
                checked={choice === 'pertinent'}
                onChange={() => setChoice('pertinent')}
              />
              <span>
                <span className="font-medium">Pertinente</span>
                <span className="block text-muted-foreground text-xs">El expediente sigue el proceso de gestión registrado.</span>
              </span>
            </label>
            <label
              className={`flex cursor-pointer gap-3 rounded-md border p-3 text-sm transition-colors ${
                choice === 'dismissed' ? 'border-destructive bg-destructive/5 ring-1 ring-destructive' : 'border-border hover:bg-muted/40'
              }`}
            >
              <input
                type="radio"
                name="qms-review-outcome"
                className="mt-0.5"
                checked={choice === 'dismissed'}
                onChange={() => setChoice('dismissed')}
              />
              <span>
                <span className="font-medium">Desestimar</span>
                <span className="block text-muted-foreground text-xs">Cierra el registro sin seguimiento (debe indicar el motivo).</span>
              </span>
            </label>
          </div>
          {choice === 'dismissed' && (
            <textarea
              className="w-full min-h-[72px] rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              placeholder="Motivo de la desestimación (obligatorio)"
              value={dismissReasonInput}
              onChange={(e) => setDismissReasonInput(e.target.value)}
            />
          )}
          <button
            type="button"
            disabled={
              saving || !choice || (choice === 'dismissed' && dismissReasonInput.trim().length === 0)
            }
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            onClick={async () => {
              if (!choice) return;
              setSaving(true);
              try {
                await postReview(
                  openingId,
                  choice,
                  choice === 'dismissed' ? dismissReasonInput.trim() : undefined
                );
                await onDone();
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Guardando…' : 'Guardar decisión'}
          </button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          La decisión de pertinencia ya está registrada como «{labelQmsReview(outcome)}».
        </p>
      )}
    </section>
  );
}

type AssigneeSelection = { kind: 'user'; userId: number; label: string };

function AssigneeSearchCombo({
  openingId,
  value,
  onChange,
}: {
  openingId: string;
  value: AssigneeSelection | null;
  onChange: (v: AssigneeSelection | null) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [users, setUsers] = useState<{ id: number; email: string }[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 280);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchQmsAssigneeOptions(openingId, debouncedQ);
        if (!cancelled) {
          setUsers(d.users);
        }
      } catch {
        if (!cancelled) {
          setUsers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openingId, debouncedQ]);

  const items = useMemo(() => {
    return users.map((user) => ({
      key: `u:${user.id}`,
      userId: user.id,
      label: user.email,
    }));
  }, [users]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border border-input bg-background px-2 py-2 text-left text-sm"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? '' : 'text-muted-foreground'}>
          {value ? value.label : 'Buscar persona responsable…'}
        </span>
        <span className="text-muted-foreground text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {value && (
        <button
          type="button"
          className="mt-1 text-xs text-muted-foreground underline"
          onClick={() => onChange(null)}
        >
          Quitar selección
        </button>
      )}
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-hidden rounded-md border border-border bg-card shadow-md">
          <input
            className="w-full border-b border-border bg-background px-2 py-2 text-sm outline-none"
            placeholder="Buscar por correo electrónico"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <ul className="max-h-48 overflow-y-auto text-sm">
            {items.length === 0 ? (
              <li className="px-3 py-2 text-muted-foreground">Sin resultados</li>
            ) : (
              items.map((it) => (
                <li key={it.key}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-muted/60"
                    onClick={() => {
                      onChange({ kind: 'user', userId: it.userId, label: it.label });
                      setOpen(false);
                      setQ('');
                    }}
                  >
                    {it.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatResourcesPreview(resources: unknown): string {
  if (resources == null) return '';
  if (typeof resources === 'object' && resources !== null && 'description' in resources) {
    return String((resources as { description?: unknown }).description ?? '');
  }
  try {
    return JSON.stringify(resources);
  } catch {
    return String(resources);
  }
}

function ParticipantMultiPicker({
  openingId,
  participants,
  onChange,
}: {
  openingId: string;
  participants: { userId: number; label: string }[];
  onChange: (p: { userId: number; label: string }[]) => void;
}) {
  const [comboKey, setComboKey] = useState(0);
  return (
    <div className="space-y-2">
      {participants.length > 0 && (
        <ul className="flex flex-wrap gap-2 text-sm">
          {participants.map((p) => (
            <li
              key={p.userId}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5"
            >
              <span>{p.label}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(participants.filter((x) => x.userId !== p.userId))}
                aria-label={`Quitar ${p.label}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <AssigneeSearchCombo
        key={comboKey}
        openingId={openingId}
        value={null}
        onChange={(sel) => {
          if (sel && !participants.some((x) => x.userId === sel.userId)) {
            onChange([...participants, { userId: sel.userId, label: sel.label }]);
            setComboKey((k) => k + 1);
          }
        }}
      />
      <p className="text-xs text-muted-foreground">Personas que participaron y avalaron el análisis de causa raíz.</p>
    </div>
  );
}

function ContingencyCloseForm({
  actionId,
  onRefresh,
  onMsg,
}: {
  actionId: string;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
}) {
  const [notes, setNotes] = useState('');
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button type="button" className="mt-1 text-xs text-primary" onClick={() => setOpen(true)}>
        Cerrar contingencia
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-border p-2">
      <label className="text-xs text-muted-foreground">Nota de cierre (obligatoria)</label>
      <textarea
        className="w-full min-h-[56px] border rounded px-2 py-1 text-xs"
        placeholder="Describa qué se hizo y el resultado de la contingencia"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!notes.trim()}
          className="text-xs rounded bg-primary text-primary-foreground px-2 py-1 disabled:opacity-50"
          onClick={async () => {
            if (!notes.trim()) {
              onMsg('Escriba una nota de cierre');
              return;
            }
            await patchContingencyDone(actionId, notes.trim());
            await onRefresh();
            onMsg('Contingencia cerrada');
          }}
        >
          Confirmar cierre
        </button>
        <button type="button" className="text-xs text-muted-foreground" onClick={() => setOpen(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export function ContingencySection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as any;
  const [what, setWhat] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [resourcesText, setResourcesText] = useState('');
  const [assignee, setAssignee] = useState<AssigneeSelection | null>(null);
  const canEdit = !blockedMessage;
  const canSubmit =
    canEdit && Boolean(what.trim()) && Boolean(dueAt) && Boolean(resourcesText.trim()) && Boolean(assignee);
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">2. Acción de contingencia</h2>
      <p className="text-sm text-muted-foreground">
        Medida rápida (días o pocas semanas) para corregir el problema puntual: qué hacer, quién lo hace, cuándo y con
        qué medios.
      </p>
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}
      {o.contingencyActions?.map((a: any) => (
        <div key={a.id} className="text-sm border-b border-border pb-2 mb-2">
          <p className="font-medium">{a.what}</p>
          {formatResourcesPreview(a.resources) ? (
            <p className="text-xs text-muted-foreground mt-1">Recursos: {formatResourcesPreview(a.resources)}</p>
          ) : null}
          <p className="text-xs text-muted-foreground mt-1">
            Responsable: {a.assignedUserEmail ?? '—'}
          </p>
          <p className="text-muted-foreground">Estado: {labelQmsActionStatus(a.status)}</p>
          {a.closureNotes && (
            <p className="text-xs text-muted-foreground mt-1">Cierre: {a.closureNotes}</p>
          )}
          {a.followUps?.length > 0 && (
            <ul className="mt-2 text-xs text-muted-foreground space-y-1">
              {a.followUps.slice(0, 5).map((n: any) => (
                <li key={n.id}>
                  {new Date(n.createdAt).toLocaleString()}: {n.note}
                  {n.plannedReviewDate ? ` · Próx. revisión: ${new Date(n.plannedReviewDate).toLocaleDateString()}` : ''}
                </li>
              ))}
            </ul>
          )}
          {canEdit && a.status === 'pending' && (
            <ContingencyCloseForm actionId={a.id} onRefresh={onRefresh} onMsg={onMsg} />
          )}
        </div>
      ))}
      {canEdit && (
        <>
          <input
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Qué hacer (obligatorio)"
            value={what}
            onChange={(e) => setWhat(e.target.value)}
          />
          <input
            type="datetime-local"
            className="w-full border rounded px-2 py-1 text-sm"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Recursos necesarios (obligatorio)
            </label>
            <textarea
              className="w-full min-h-[64px] border rounded px-2 py-1 text-sm"
              placeholder="Materiales, tiempo, equipos o apoyo necesario"
              value={resourcesText}
              onChange={(e) => setResourcesText(e.target.value)}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Persona responsable</p>
            <AssigneeSearchCombo openingId={o.id} value={assignee} onChange={setAssignee} />
          </div>
          <button
            type="button"
            disabled={!canSubmit}
            className="rounded-md bg-secondary px-3 py-1.5 text-sm disabled:opacity-50"
            onClick={async () => {
              if (!what.trim() || !dueAt || !resourcesText.trim()) {
                onMsg('Complete qué se hará, cuándo y los recursos necesarios');
                return;
              }
              if (!assignee) {
                onMsg('Seleccione la persona responsable');
                return;
              }
              await postContingency(o.id, {
                what: what.trim(),
                dueAt: new Date(dueAt).toISOString(),
                assignedUserId: assignee.userId,
                resources: { description: resourcesText.trim() },
              });
              setWhat('');
              setDueAt('');
              setResourcesText('');
              setAssignee(null);
              await onRefresh();
              onMsg('Contingencia registrada');
            }}
          >
            Registrar acción de contingencia
          </button>
        </>
      )}
    </section>
  );
}

export function VerificationSection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as any;
  const [notes, setNotes] = useState('');
  const canEdit = !blockedMessage;
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <h2 className="font-medium">3. Verificación de la contingencia</h2>
      <p className="text-sm text-muted-foreground">
        Cuando las acciones de contingencia estén hechas, compruebe si el desvío desapareció. Solo después se evalúa si
        hace falta una acción correctiva (análisis profundo).
      </p>
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}
      {o.contingencyVerifications?.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-1 border-b border-border pb-2">
          {o.contingencyVerifications.map((v: any) => (
            <li key={v.id}>
              {new Date(v.verifiedAt).toLocaleString()}: {v.eliminatedDeviation ? 'Desvío eliminado' : 'Aún hay desvío'}
              {v.notes ? ` — ${v.notes}` : ''}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <>
          <label className="text-xs text-muted-foreground">Notas de la verificación</label>
          <textarea
            className="w-full border rounded px-2 py-1 text-sm min-h-[72px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Detalle de la verificación y evidencia observada…"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="text-sm rounded bg-primary text-primary-foreground px-3 py-1"
              onClick={async () => {
                await postContingencyVerification(o.id, { eliminatedDeviation: true, notes });
                await onRefresh();
                onMsg('Verificación registrada');
              }}
            >
              Confirmar: desvío eliminado
            </button>
            <button
              type="button"
              className="text-sm rounded border px-3 py-1"
              onClick={async () => {
                await postContingencyVerification(o.id, { eliminatedDeviation: false, notes });
                await onRefresh();
                onMsg('Verificación registrada');
              }}
            >
              Aún hay desvío
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function CorrectiveDecisionSection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as QmsOpeningDecisionInput & { id: string; originType?: string };
  const [waiveObs, setWaiveObs] = useState('');
  const [waiveDialogOpen, setWaiveDialogOpen] = useState(false);
  const summary = resolveCorrectiveDecisionSummary(o);
  const canDecide = !blockedMessage && isCorrectiveDecisionActive(o.status);

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <h2 className="font-medium">4. ¿Hace falta acción correctiva?</h2>
      <p className="text-sm text-muted-foreground">
        La acción correctiva es distinta de la contingencia: busca la causa raíz y evita que el problema vuelva (plazos
        más largos: semanas o meses). Solo corresponde si aquí se decide que aplica.
      </p>
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}
      {summary ? (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
          <p className="font-medium">Decisión registrada</p>
          <dl className="grid gap-1.5 text-xs sm:grid-cols-[auto_1fr] sm:gap-x-3">
            <dt className="text-muted-foreground">Decisión</dt>
            <dd>{summary.decision}</dd>
            {summary.decidedBy ? (
              <>
                <dt className="text-muted-foreground">Registrada por</dt>
                <dd>{summary.decidedBy}</dd>
              </>
            ) : null}
            {summary.decidedAt ? (
              <>
                <dt className="text-muted-foreground">Fecha</dt>
                <dd>
                  <DateCell value={summary.decidedAt} />
                </dd>
              </>
            ) : null}
            {summary.justification ? (
              <>
                <dt className="text-muted-foreground">Justificación</dt>
                <dd className="whitespace-pre-wrap">{summary.justification}</dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : canDecide && o.originType === 'external' ? (
        <>
          <p className="text-xs text-muted-foreground">
            Origen externo: la AC está prevista por defecto. Si no se aplica, debe justificarse.
          </p>
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1 mr-2"
            onClick={async () => {
              await postCorrectiveDecision(o.id, { startCorrective: true });
              await onRefresh();
              onMsg('Se confirma seguimiento de acción correctiva');
            }}
          >
            Confirmar seguimiento AC
          </button>
          <button
            type="button"
            className="text-sm rounded border px-3 py-1 mt-1"
            onClick={() => setWaiveDialogOpen(true)}
          >
            No aplicar AC (justificar)
          </button>
          {waiveDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div
                className="w-full max-w-md rounded-lg border border-border bg-card p-4 space-y-3 shadow-lg"
                role="dialog"
                aria-labelledby="waive-ac-title"
              >
                <h3 id="waive-ac-title" className="font-medium text-sm">
                  Justificación obligatoria — no aplicar acción correctiva
                </h3>
                <p className="text-xs text-muted-foreground">
                  Origen externo: debe explicar por qué no corresponde tomar acción correctiva en este reclamo.
                </p>
                <textarea
                  className="w-full min-h-[88px] border rounded px-2 py-1 text-sm"
                  placeholder="Observación obligatoria"
                  value={waiveObs}
                  onChange={(e) => setWaiveObs(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="text-sm rounded border px-3 py-1"
                    onClick={() => {
                      setWaiveDialogOpen(false);
                      setWaiveObs('');
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={waiveObs.trim().length === 0}
                    className="text-sm rounded bg-primary text-primary-foreground px-3 py-1 disabled:opacity-50"
                    onClick={async () => {
                      const obs = waiveObs.trim();
                      if (!obs) {
                        onMsg('Escriba la observación obligatoria para no aplicar AC');
                        return;
                      }
                      await postCorrectiveDecision(o.id, {
                        startCorrective: false,
                        waiveCorrective: true,
                        waiveObservation: obs,
                      });
                      setWaiveDialogOpen(false);
                      setWaiveObs('');
                      await onRefresh();
                      onMsg('Decisión registrada: no aplica acción correctiva');
                    }}
                  >
                    Confirmar: no aplicar AC
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : canDecide ? (
        <>
          <p className="text-xs text-muted-foreground">
            Origen interno: la acción correctiva es opcional salvo desviación grave o recurrente.
          </p>
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1 mr-2"
            onClick={async () => {
              await postCorrectiveDecision(o.id, {
                startCorrective: true,
                internalTriggersCorrective: true,
              });
              await onRefresh();
              onMsg('Se inicia AC (grave/recurrente)');
            }}
          >
            Sí, iniciar acción correctiva (grave o recurrente)
          </button>
          <button
            type="button"
            className="text-sm rounded border px-3 py-1"
            onClick={async () => {
              await postCorrectiveDecision(o.id, { startCorrective: false });
              await onRefresh();
              onMsg('Decisión registrada: no requiere acción correctiva');
            }}
          >
            No, no aplicar acción correctiva
          </button>
        </>
      ) : null}
    </section>
  );
}

function CorrectiveActionRow({
  action: a,
  onRefresh,
  onMsg,
}: {
  action: any;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
}) {
  const [eff, setEff] = useState('');
  const [efficacyResult, setEfficacyResult] = useState<'eliminated' | 'minimized' | 'failed' | ''>('');
  const [closureReason, setClosureReason] = useState('');
  const [showCloseWithout, setShowCloseWithout] = useState(false);
  const [trainingRef, setTrainingRef] = useState('');
  const [trainingSessionId, setTrainingSessionId] = useState('');
  const [trainingSessions, setTrainingSessions] = useState<TrainingSession[]>([]);
  const actionClosed = isCorrectiveActionClosedUi(a);

  useEffect(() => {
    void fetchTrainings()
      .then(setTrainingSessions)
      .catch(() => setTrainingSessions([]));
  }, []);
  return (
    <div className="text-sm border-b border-border pb-3 mb-3 space-y-2">
      <p className="font-medium">{a.what}</p>
      {a.rootCause?.description && (
        <p className="text-xs text-muted-foreground">Causa raíz: {a.rootCause.description}</p>
      )}
      {formatResourcesPreview(a.resources) ? (
        <p className="text-xs text-muted-foreground">Recursos: {formatResourcesPreview(a.resources)}</p>
      ) : null}
      <p className="text-muted-foreground">
        Responsable: {a.assignedUserEmail ?? '—'}
      </p>
      <p className="text-muted-foreground">Estado: {labelCorrectiveActionLifecycle(a)}</p>
      {a.efficacyResult && (
        <p className="text-xs text-muted-foreground">Resultado eficacia: {labelEfficacyResult(a.efficacyResult)}</p>
      )}
      {a.closureReason && (
        <p className="text-xs text-muted-foreground">Motivo cierre sin eficacia: {a.closureReason}</p>
      )}
      {a.efficacyVerifiedAt && a.rootCauseEliminated != null && (
        <p className="text-xs">
          <span className="font-medium">¿Se eliminó la causa raíz?</span>{' '}
          <span className={a.rootCauseEliminated ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
            {a.rootCauseEliminated ? 'Sí' : 'No'}
          </span>
        </p>
      )}
      {a.participants?.length > 0 && (
        <p className="text-xs">
          Participaron en el análisis de causa:{' '}
          {a.participants.map((p: any) => p.userEmail ?? `usuario #${p.userId}`).join(', ')}
        </p>
      )}
      {a.trainingLinks?.length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc pl-4">
          {a.trainingLinks.map((t: any) => (
            <li key={t.id}>
              Capacitación: {t.trainingSession?.title ?? t.externalPlanRef}
              {t.trainingSession?.scheduledDate
                ? ` (${new Date(t.trainingSession.scheduledDate).toLocaleDateString()})`
                : ''}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2 items-end">
        <select
          className="min-w-[12rem] border rounded px-2 py-1 text-xs bg-background"
          value={trainingSessionId}
          onChange={(e) => setTrainingSessionId(e.target.value)}
        >
          <option value="">Sesión de capacitación existente…</option>
          {trainingSessions.map((session) => (
            <option key={session.id} value={session.id}>
              {session.title} — {new Date(session.scheduledDate).toLocaleDateString()}
            </option>
          ))}
        </select>
        <input
          className="flex-1 min-w-[10rem] border rounded px-2 py-1 text-xs"
          placeholder="O referencia textual (legado)"
          value={trainingRef}
          onChange={(e) => setTrainingRef(e.target.value)}
        />
        <button
          type="button"
          className="text-xs rounded border px-2 py-1"
          disabled={!trainingSessionId && !trainingRef.trim()}
          onClick={async () => {
            await postTrainingPlanLink(a.id, {
              trainingSessionId: trainingSessionId || undefined,
              externalPlanRef: trainingRef.trim() || undefined,
            });
            setTrainingRef('');
            setTrainingSessionId('');
            await onRefresh();
            onMsg('Vínculo con plan de capacitación guardado');
          }}
        >
          Enlazar
        </button>
      </div>
      {a.followUps?.length > 0 && (
        <ul className="text-xs border-t border-border pt-2 space-y-1">
          {a.followUps.map((n: any) => (
            <li key={n.id}>
              <span className="text-muted-foreground">{new Date(n.createdAt).toLocaleString()}</span> — {n.note}
              {n.plannedReviewDate ? ` · Próx. revisión: ${new Date(n.plannedReviewDate).toLocaleDateString()}` : ''}
            </li>
          ))}
        </ul>
      )}
      {!a.implementedAt && a.status !== 'cancelled' && (
        <button
          type="button"
          className="text-xs text-primary mr-2"
          onClick={async () => {
            await patchCorrectiveImplemented(a.id);
            await onRefresh();
            onMsg('Implementación registrada');
          }}
        >
          Marcar implementada
        </button>
      )}
      {a.implementedAt && !actionClosed && (
        <div className="mt-2 space-y-2 rounded-md border border-border p-2">
          <p className="text-xs font-medium">Verificación de eficacia</p>
          <div>
            <label className="text-xs text-muted-foreground" htmlFor={`efficacy-result-${a.id}`}>
              Resultado de la acción sobre la causa raíz (obligatorio)
            </label>
            <select
              id={`efficacy-result-${a.id}`}
              className="mt-1 w-full border rounded px-2 py-1 text-xs bg-background"
              value={efficacyResult}
              onChange={(e) => setEfficacyResult(e.target.value as typeof efficacyResult)}
            >
              <option value="">Seleccione…</option>
              <option value="eliminated">Eliminó la causa raíz</option>
              <option value="minimized">Minimizó la causa raíz</option>
              <option value="failed">No eliminó ni minimizó la causa raíz</option>
            </select>
            {efficacyResult === 'failed' && (
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                Si la eficacia falla, la acción no se cierra y se reabre el análisis de causa raíz (paso 5).
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Evidencia de eficacia (obligatoria)</label>
            <textarea
              className="w-full min-h-[56px] border rounded px-2 py-1 text-xs"
              placeholder="Describa la evidencia que respalda la verificación"
              value={eff}
              onChange={(e) => setEff(e.target.value)}
            />
          </div>
          <button
            type="button"
            disabled={!eff.trim() || !efficacyResult}
            className="text-xs rounded bg-primary text-primary-foreground px-2 py-1 disabled:opacity-50"
            onClick={async () => {
              if (!eff.trim() || !efficacyResult) {
                onMsg('Complete el resultado y la evidencia de eficacia');
                return;
              }
              await postCorrectiveEfficacy(a.id, eff.trim(), efficacyResult);
              setEff('');
              setEfficacyResult('');
              await onRefresh();
              onMsg(
                efficacyResult === 'failed'
                  ? 'Eficacia fallida: reabra el análisis de causa raíz'
                  : 'Eficacia verificada'
              );
            }}
          >
            Verificar eficacia
          </button>
          {!showCloseWithout ? (
            <button
              type="button"
              className="text-xs rounded border px-2 py-1 ml-2"
              onClick={() => setShowCloseWithout(true)}
            >
              Cerrar sin verificar eficacia
            </button>
          ) : (
            <div className="mt-2 space-y-2 border-t border-border pt-2">
              <label className="text-xs text-muted-foreground">Motivo del cierre sin eficacia (obligatorio)</label>
              <textarea
                className="w-full min-h-[56px] border rounded px-2 py-1 text-xs"
                value={closureReason}
                onChange={(e) => setClosureReason(e.target.value)}
                placeholder="Explique por qué se cierra sin verificar eficacia…"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!closureReason.trim()}
                  className="text-xs rounded border px-2 py-1 disabled:opacity-50"
                  onClick={async () => {
                    await postCloseCorrectiveWithoutEfficacy(a.id, closureReason.trim());
                    setClosureReason('');
                    setShowCloseWithout(false);
                    await onRefresh();
                    onMsg('Acción cerrada sin verificar eficacia');
                  }}
                >
                  Confirmar cierre
                </button>
                <button
                  type="button"
                  className="text-xs text-muted-foreground"
                  onClick={() => {
                    setShowCloseWithout(false);
                    setClosureReason('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ROOT_CAUSE_METHODS = [
  '5 por qué',
  'Diagrama de Ishikawa (causa-efecto)',
  'Análisis de Pareto',
  'Árbol de fallas',
  'Diagrama de afinidad',
  'Otro',
];

type StepDraft = {
  clientId: string;
  order: number;
  tool: string;
  customTool: string;
  inputText: string;
  outputText: string;
  notes: string;
};

type CauseDraft = {
  clientId: string;
  description: string;
};

function jsonTextField(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object' && value !== null && 'text' in value) {
    return String((value as { text?: unknown }).text ?? '');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

function CycleHistoryPanel({ cycle }: { cycle: any }) {
  return (
    <details className="rounded-md border border-border bg-muted/20 p-3 text-sm">
      <summary className="cursor-pointer font-medium">
        Ciclo {cycle.cycleNumber} — {labelAnalysisCycleReason(cycle.reason)} (
        {new Date(cycle.createdAt).toLocaleString()})
      </summary>
      <div className="mt-2 space-y-2 text-xs">
        <div>
          <p className="font-medium">Pasos</p>
          <ul className="list-disc pl-4 space-y-1">
            {(cycle.steps ?? []).map((s: any) => (
              <li key={s.id}>
                {s.order}. {s.tool}
                {s.outputData ? ` — Salida: ${jsonTextField(s.outputData)}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-medium">Causas raíz</p>
          <ul className="list-disc pl-4 space-y-1">
            {(cycle.rootCauses ?? []).map((c: any) => (
              <li key={c.id}>{c.description}</li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

export function RootCauseAnalysisSection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as any;
  const cycles = (o.rootCauseAnalysisCycles ?? []) as any[];
  const activeCycle = getActiveRootCauseCycle(cycles);
  const priorCycles = cycles.filter((c) => c.id !== activeCycle?.id);
  const participantCount = o.rootCauseParticipants?.length ?? 0;
  const analysisComplete = isRootCauseAnalysisCompleteUi(activeCycle, participantCount);

  const [steps, setSteps] = useState<StepDraft[]>([
    { clientId: '1', order: 1, tool: '', customTool: '', inputText: '', outputText: '', notes: '' },
  ]);
  const [rootCauses, setRootCauses] = useState<CauseDraft[]>([{ clientId: '1', description: '' }]);
  const [participants, setParticipants] = useState<{ userId: number; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiDraftLoaded, setAiDraftLoaded] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const canEdit = !blockedMessage;

  const lastEfficacyFailure = (o.dynamicData as Record<string, unknown> | undefined)?._lastEfficacyFailure as
    | { at?: string; notes?: string }
    | undefined;

  const canSubmit =
    canEdit &&
    steps.every((s) => (s.tool === 'Otro' ? s.customTool.trim() : s.tool.trim())) &&
    rootCauses.every((c) => c.description.trim()) &&
    participants.length > 0;

  const updateStep = (clientId: string, patch: Partial<StepDraft>) => {
    setSteps((prev) => prev.map((s) => (s.clientId === clientId ? { ...s, ...patch } : s)));
  };

  const usePreviousOutput = (index: number) => {
    if (index <= 0) return;
    const prevOutput = steps[index - 1]?.outputText ?? '';
    updateStep(steps[index].clientId, { inputText: prevOutput });
  };

  const buildPayload = () => ({
    participantUserIds: participants.map((p) => p.userId),
    steps: steps.map((s, index) => ({
      order: index + 1,
      tool: (s.tool === 'Otro' ? s.customTool : s.tool).trim(),
      inputData: s.inputText.trim() ? { text: s.inputText.trim() } : null,
      outputData: s.outputText.trim() ? { text: s.outputText.trim() } : null,
      notes: s.notes.trim() || undefined,
    })),
    rootCauses: rootCauses.map((c, index) => ({
      description: c.description.trim(),
      order: index,
    })),
  });

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">5. Análisis de causa raíz</h2>
      <p className="text-sm text-muted-foreground">
        Documente el análisis por pasos (herramientas en cascada) y participantes; al final registre una o más causas
        raíz como conclusión del análisis.
      </p>
      {lastEfficacyFailure?.at && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          La verificación de eficacia anterior no logró eliminar ni minimizar la causa raíz (
          {new Date(lastEfficacyFailure.at).toLocaleString()}). Complete un nuevo ciclo de análisis.
          {lastEfficacyFailure.notes ? ` Evidencia: ${lastEfficacyFailure.notes}` : ''}
        </p>
      )}
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}

      {priorCycles.map((cycle) => (
        <CycleHistoryPanel key={cycle.id} cycle={cycle} />
      ))}

      {analysisComplete && activeCycle && (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
          <p className="font-medium">
            Ciclo activo {activeCycle.cycleNumber} — {labelAnalysisCycleReason(activeCycle.reason)}
          </p>
          <div>
            <p className="text-xs font-medium text-muted-foreground">Pasos</p>
            <ul className="text-xs list-disc pl-4">
              {(activeCycle.steps ?? []).map((s: any) => (
                <li key={s.id}>
                  {s.order}. {s.tool}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">Causas raíz</p>
            <ul className="text-xs list-disc pl-4">
              {(activeCycle.rootCauses ?? []).map((c: any) => (
                <li key={c.id}>{c.description}</li>
              ))}
            </ul>
          </div>
          {o.rootCauseParticipants?.length > 0 && (
            <p className="text-xs">
              <span className="font-medium">Participantes:</span>{' '}
              {o.rootCauseParticipants.map((p: any) => p.userEmail ?? `usuario #${p.userId}`).join(', ')}
            </p>
          )}
        </div>
      )}

      {canEdit && (
        <div className="border-t border-border pt-3 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium">{analysisComplete ? 'Actualizar ciclo activo' : 'Registrar análisis'}</p>
            <button
              type="button"
              disabled={suggesting}
              className="rounded-md border border-primary px-3 py-1.5 text-sm text-primary disabled:opacity-50"
              onClick={async () => {
                setSuggesting(true);
                try {
                  const result = await suggestRootCause({ ncId: o.id });
                  if (result.draft.steps?.length) {
                    setSteps(
                      result.draft.steps.map((step, index) => ({
                        clientId: String(Date.now() + index),
                        order: step.order ?? index + 1,
                        tool: step.tool,
                        customTool: '',
                        inputText: step.inputText ?? '',
                        outputText: step.outputText ?? '',
                        notes: step.notes ?? '',
                      })),
                    );
                  }
                  if (result.draft.rootCauses?.length) {
                    setRootCauses(
                      result.draft.rootCauses.map((cause, index) => ({
                        clientId: String(Date.now() + index + 100),
                        description: cause.description,
                      })),
                    );
                  }
                  setAiSummary(result.draft.summary?.trim() ?? '');
                  setAiDraftLoaded(true);
                  onMsg('Borrador de causa raíz generado — revise antes de guardar');
                } catch (err: unknown) {
                  const message =
                    err && typeof err === 'object' && 'response' in err
                      ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                      : null;
                  onMsg(message ?? 'No se pudo generar la sugerencia de causa raíz');
                } finally {
                  setSuggesting(false);
                }
              }}
            >
              {suggesting ? 'Generando sugerencia…' : 'Sugerir causa raíz'}
            </button>
          </div>

          {aiDraftLoaded && (
            <div className="space-y-2">
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                Borrador generado por IA — revise y edite antes de guardar en el formulario.
              </p>
              {aiSummary && (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs font-medium text-muted-foreground">Resumen sugerido por IA (referencia, no se guarda)</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{aiSummary}</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground">Pasos de análisis (herramientas en cascada)</p>
            {steps.map((step, index) => (
              <div key={step.clientId} className="rounded-md border border-border p-3 space-y-2">
                <p className="text-xs font-medium">Paso {index + 1}</p>
                <select
                  className="w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                  value={step.tool}
                  onChange={(e) => updateStep(step.clientId, { tool: e.target.value })}
                >
                  <option value="">Seleccione herramienta…</option>
                  {ROOT_CAUSE_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                {step.tool === 'Otro' && (
                  <input
                    className="w-full border rounded px-2 py-1 text-sm"
                    placeholder="Especifique la herramienta"
                    value={step.customTool}
                    onChange={(e) => updateStep(step.clientId, { customTool: e.target.value })}
                  />
                )}
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs text-muted-foreground">Entrada del paso</label>
                    {index > 0 && (
                      <button
                        type="button"
                        className="text-xs text-primary"
                        onClick={() => usePreviousOutput(index)}
                      >
                        Usar salida anterior como entrada
                      </button>
                    )}
                  </div>
                  <textarea
                    className="w-full min-h-[48px] border rounded px-2 py-1 text-xs"
                    value={step.inputText}
                    onChange={(e) => updateStep(step.clientId, { inputText: e.target.value })}
                    placeholder="Datos o contexto de entrada del paso…"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Salida / resultado del paso</label>
                  <textarea
                    className="w-full min-h-[48px] border rounded px-2 py-1 text-xs"
                    value={step.outputText}
                    onChange={(e) => updateStep(step.clientId, { outputText: e.target.value })}
                    placeholder="Resultado o hallazgo del paso…"
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="text-xs rounded border px-2 py-1"
              onClick={() =>
                setSteps((prev) => [
                  ...prev,
                  {
                    clientId: String(Date.now()),
                    order: prev.length + 1,
                    tool: '',
                    customTool: '',
                    inputText: '',
                    outputText: '',
                    notes: '',
                  },
                ])
              }
            >
              Agregar paso
            </button>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Personas que participaron del análisis (obligatorio)</label>
            <ParticipantMultiPicker openingId={o.id} participants={participants} onChange={setParticipants} />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Causas raíz identificadas (conclusión)</p>
            {rootCauses.map((cause) => (
              <div key={cause.clientId} className="flex gap-2 items-start">
                <textarea
                  className="flex-1 min-h-[56px] border rounded px-2 py-1 text-sm"
                  placeholder="Descripción de la causa raíz"
                  value={cause.description}
                  onChange={(e) =>
                    setRootCauses((prev) =>
                      prev.map((c) => (c.clientId === cause.clientId ? { ...c, description: e.target.value } : c))
                    )
                  }
                />
                {rootCauses.length > 1 && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground shrink-0"
                    onClick={() => setRootCauses((prev) => prev.filter((c) => c.clientId !== cause.clientId))}
                  >
                    Quitar
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="text-xs rounded border px-2 py-1"
              onClick={() => setRootCauses((prev) => [...prev, { clientId: String(Date.now()), description: '' }])}
            >
              Agregar causa raíz
            </button>
          </div>

          <button
            type="button"
            disabled={!canSubmit || saving}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            onClick={async () => {
              if (!canSubmit) {
                onMsg('Complete pasos, participantes y al menos una causa raíz');
                return;
              }
              setSaving(true);
              try {
                await postRootCauseAnalysis(o.id, buildPayload());
                setSteps([
                  { clientId: '1', order: 1, tool: '', customTool: '', inputText: '', outputText: '', notes: '' },
                ]);
                setRootCauses([{ clientId: '1', description: '' }]);
                setParticipants([]);
                setAiSummary('');
                setAiDraftLoaded(false);
                await onRefresh();
                onMsg('Análisis de causa raíz registrado');
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Guardando…' : analysisComplete ? 'Actualizar análisis' : 'Guardar análisis'}
          </button>
        </div>
      )}
    </section>
  );
}

type RootCauseCoverageItem = {
  rootCauseId: string;
  description: string;
  correctiveActionCount: number;
  hasOpenAction: boolean;
};

function coverageCountLabel(count: number): string {
  if (count === 0) return 'sin acción correctiva';
  return count === 1 ? '1 acción correctiva' : `${count} acciones correctivas`;
}

export function CorrectiveActionsSection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as {
    id: string;
    rootCauseAnalysisCycles?: QmsRootCauseCycleRef[];
    rootCauseParticipants?: unknown[];
    correctiveActions?: unknown[];
    rootCauseCoverage?: RootCauseCoverageItem[];
  };
  const [what, setWhat] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [resourcesText, setResourcesText] = useState('');
  const [assignee, setAssignee] = useState<AssigneeSelection | null>(null);
  const [rootCauseId, setRootCauseId] = useState('');
  const canEdit = !blockedMessage;
  const activeCycle = getActiveRootCauseCycle(o.rootCauseAnalysisCycles ?? []);
  const activeRootCauses = activeCycle?.rootCauses ?? [];
  const analysisComplete = isRootCauseAnalysisCompleteUi(activeCycle, o.rootCauseParticipants?.length ?? 0);
  const coverage = o.rootCauseCoverage ?? [];
  const uncoveredCount = coverage.filter((c) => c.correctiveActionCount === 0).length;

  // Sugiere por defecto la primera causa raíz que aún no tiene acción correctiva.
  useEffect(() => {
    if (!analysisComplete || rootCauseId) return;
    const firstUncovered = coverage.find((c) => c.correctiveActionCount === 0);
    if (firstUncovered) setRootCauseId(firstUncovered.rootCauseId);
  }, [analysisComplete, coverage, rootCauseId]);
  const canSubmit =
    canEdit &&
    Boolean(what.trim()) &&
    Boolean(dueAt) &&
    Boolean(resourcesText.trim()) &&
    Boolean(assignee) &&
    Boolean(rootCauseId);
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">6. Plan de acción correctiva</h2>
      <p className="text-sm text-muted-foreground">
        Defina qué hará el equipo, quién coordina, plazo y recursos. Puede vincular capacitaciones al plan anual más
        abajo en cada acción.
      </p>
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}
      {o.correctiveActions?.map((a: any) => (
        <CorrectiveActionRow key={a.id} action={a} onRefresh={onRefresh} onMsg={onMsg} />
      ))}
      {canEdit && !analysisComplete && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          Complete primero el análisis de causa raíz (paso 5) antes de registrar acciones correctivas.
        </p>
      )}
      {analysisComplete && coverage.length > 0 && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
          <p className="text-sm font-medium">Cobertura por causa raíz</p>
          <p className="text-xs text-muted-foreground">
            Cada causa raíz debería tener al menos una acción correctiva que evite su recurrencia.
          </p>
          <ul className="space-y-1 text-sm">
            {coverage.map((c) => (
              <li key={c.rootCauseId} className="flex items-start justify-between gap-2">
                <span className="line-clamp-2">
                  {c.description.length > 90 ? `${c.description.slice(0, 90)}…` : c.description}
                </span>
                <span
                  className={
                    c.correctiveActionCount === 0
                      ? 'shrink-0 text-amber-700 dark:text-amber-400'
                      : 'shrink-0 text-green-700 dark:text-green-400'
                  }
                >
                  {coverageCountLabel(c.correctiveActionCount)}
                </span>
              </li>
            ))}
          </ul>
          {uncoveredCount > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              {uncoveredCount === 1
                ? 'Queda 1 causa raíz sin acción correctiva.'
                : `Quedan ${uncoveredCount} causas raíz sin acción correctiva.`}
            </p>
          )}
        </div>
      )}
      {canEdit && analysisComplete && (
        <>
          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-sm font-medium">Nueva acción correctiva</p>
            <select
              className="w-full border rounded px-2 py-1 text-sm bg-background"
              value={rootCauseId}
              onChange={(e) => setRootCauseId(e.target.value)}
            >
              <option value="">Seleccione la causa raíz…</option>
              {activeRootCauses.map((c: any) => {
                const cov = coverage.find((x) => x.rootCauseId === c.id);
                const suffix = cov ? ` — ${coverageCountLabel(cov.correctiveActionCount)}` : '';
                const text = c.description.length > 70 ? `${c.description.slice(0, 70)}…` : c.description;
                return (
                  <option key={c.id} value={c.id}>
                    {text}
                    {suffix}
                  </option>
                );
              })}
            </select>
            <input
              className="w-full border rounded px-2 py-1 text-sm"
              placeholder="Qué se hará (obligatorio)"
              value={what}
              onChange={(e) => setWhat(e.target.value)}
            />
            <input
              type="datetime-local"
              className="w-full border rounded px-2 py-1 text-sm"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <div>
              <label className="text-xs text-muted-foreground">Recursos necesarios (obligatorio)</label>
              <textarea
                className="w-full min-h-[64px] border rounded px-2 py-1 text-sm"
                placeholder="Materiales, tiempo, equipos o apoyo necesario"
                value={resourcesText}
                onChange={(e) => setResourcesText(e.target.value)}
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Persona responsable de la acción</p>
              <AssigneeSearchCombo openingId={o.id} value={assignee} onChange={setAssignee} />
            </div>
            <button
              type="button"
              disabled={!canSubmit}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm disabled:opacity-50"
              onClick={async () => {
                if (!what.trim() || !dueAt || !resourcesText.trim()) {
                  onMsg('Complete qué se hará, cuándo y los recursos necesarios');
                  return;
                }
                if (!assignee) {
                  onMsg('Seleccione la persona responsable');
                  return;
                }
                if (!rootCauseId) {
                  onMsg('Seleccione la causa raíz vinculada');
                  return;
                }
                await postCorrectiveAction(o.id, {
                  what: what.trim(),
                  dueAt: new Date(dueAt).toISOString(),
                  assignedUserId: assignee.userId,
                  rootCauseId,
                  resources: { description: resourcesText.trim() },
                });
                setWhat('');
                setDueAt('');
                setResourcesText('');
                setAssignee(null);
                setRootCauseId('');
                await onRefresh();
                onMsg('Acción correctiva registrada');
              }}
            >
              Registrar acción correctiva
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export function FollowUpSection({
  opening,
  onRefresh,
  onMsg,
  blockedMessage,
}: {
  opening: Record<string, unknown>;
  onRefresh: () => Promise<void>;
  onMsg: (s: string) => void;
  blockedMessage?: string | null;
}) {
  const o = opening as any;
  const [note, setNote] = useState('');
  const [plannedReviewDate, setPlannedReviewDate] = useState('');
  const [link, setLink] = useState<'general' | string>('general');
  const canEdit = !blockedMessage;

  const contingencyOpts = (o.contingencyActions ?? []).map((c: any) => ({
    id: `c:${c.id}`,
    contingencyActionId: c.id,
    label: (c.what as string).slice(0, 60) + ((c.what as string).length > 60 ? '…' : ''),
  }));
  const correctiveOpts = (o.correctiveActions ?? []).map((c: any) => ({
    id: `a:${c.id}`,
    correctiveActionId: c.id,
    label: (c.what as string).slice(0, 60) + ((c.what as string).length > 60 ? '…' : ''),
  }));

  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="font-medium">Seguimiento y notas</h2>
      <p className="text-sm text-muted-foreground">
        Cada nota queda registrada con fecha. Use la próxima fecha de revisión para planificar el control del plan. Puede
        vincular la nota a una acción concreta o dejarla general.
      </p>
      {blockedMessage && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {blockedMessage}
        </p>
      )}
      {canEdit && (
        <>
          <div>
            <label className="text-xs text-muted-foreground">Vincular a</label>
            <select
              className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            >
              <option value="general">Todo el expediente</option>
              {contingencyOpts.map((x: { id: string; label: string }) => (
                <option key={x.id} value={x.id}>
                  Contingencia: {x.label}
                </option>
              ))}
              {correctiveOpts.map((x: { id: string; label: string }) => (
                <option key={x.id} value={x.id}>
                  Acción correctiva: {x.label}
                </option>
              ))}
            </select>
          </div>
          <textarea
            className="w-full min-h-[80px] border rounded px-2 py-1 text-sm"
            placeholder="Nota o seguimiento"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div>
            <label className="text-xs text-muted-foreground">Próxima revisión (opcional)</label>
            <input
              type="date"
              className="w-full border rounded px-2 py-1 text-sm"
              value={plannedReviewDate}
              onChange={(e) => setPlannedReviewDate(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            onClick={async () => {
              if (!note.trim()) {
                onMsg('Escriba una nota');
                return;
              }
              let contingencyActionId: string | null | undefined;
              let correctiveActionId: string | null | undefined;
              if (link === 'general') {
                contingencyActionId = undefined;
                correctiveActionId = undefined;
              } else if (link.startsWith('c:')) {
                contingencyActionId = link.slice(2);
                correctiveActionId = undefined;
              } else if (link.startsWith('a:')) {
                correctiveActionId = link.slice(2);
                contingencyActionId = undefined;
              }
              await postFollowUpNote(o.id, {
                note: note.trim(),
                plannedReviewDate: plannedReviewDate ? new Date(plannedReviewDate).toISOString() : null,
                contingencyActionId,
                correctiveActionId,
              });
              setNote('');
              setPlannedReviewDate('');
              await onRefresh();
              onMsg('Nota de seguimiento guardada');
            }}
          >
            Guardar nota
          </button>
        </>
      )}
    </section>
  );
}

export function ExternalCommSection({ openingId, onMsg }: { openingId: string; onMsg: (s: string) => void }) {
  const [channel, setChannel] = useState<'email' | 'whatsapp' | 'other'>('email');
  const [recipient, setRecipient] = useState('');
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <h2 className="font-medium">Comunicación formal al proveedor/transportista</h2>
      <select
        className="border rounded px-2 py-1 text-sm"
        value={channel}
        onChange={(e) => setChannel(e.target.value as 'email' | 'whatsapp' | 'other')}
      >
        <option value="email">Email</option>
        <option value="whatsapp">WhatsApp</option>
        <option value="other">Otro</option>
      </select>
      <input
        className="w-full border rounded px-2 py-1 text-sm"
        placeholder="Destinatario"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
      />
      <button
        type="button"
        className="text-sm rounded bg-primary text-primary-foreground px-3 py-1"
        onClick={async () => {
          await postExternalCommunication(openingId, { channel, recipient });
          onMsg('Comunicación registrada (envío pendiente de integración)');
        }}
      >
        Registrar comunicación
      </button>
    </section>
  );
}
